/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect } from 'react';

import IEnergyManagementProps, {
  ICarbonAvoidanceData,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import TotalEmissionsDetailsComponent from '../../CarbonSiteViewDetailsComponent/TotalEmissionDetailWidget/TotalEmissionDetailWidget';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import {
  formattedDate,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import { UtilityType } from '../../../shared/Utils/Constants';
import PortfolioCarbonScopeTwoSavingsBySiteComponent from '../PortfolioCarbonScopeTwoSavingsBySiteWidget/PortfolioCarbonScopeTwoSavingsBySiteComponent';
import { getCarbonVDEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ApiContext } from '../../../../context/ApiContext';
import CarbonAvoidanceService from '../PortfolioCarbonAvoidance/CarbonAvoidanceService';
import CarbonAvoidanceSummaryWidget from '../PortfolioCarbonAvoidance/CarbonAvoidanceSummaryWidget';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  SmartPowerSubscriptionType
} from '../../../shared/Enums';
import PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate from '../PortfolioCarbonScopeTwoSavingsBySiteWidget/PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate';
import TotalEmissionDetailWidgetSeparatePortfolio from '../../CarbonSiteViewDetailsComponent/TotalEmissionDetailWidget/TotalEmissionDetailWidgetSeparatePortfolio';

const PortfolioTotalEmissionsDetailsComponent = (
  props: IEnergyManagementProps
) => {
  const { bearerToken, requestContext } = props;
  const isMounted = useMounted();
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;
  const apiContext = useContext(ApiContext);
  const dataSource = requestContext.context.dataSource || '';

  let connectorConfig: any;
  let connectorConfigForGraph: any;
  let connectorConfigForTable: any;

  const isPremiumSubscription =
    requestContext.context.smpSubscriptionType ===
    SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
      ? true
      : false;
  const isSitePremium = apiContext.toggleSeparateApi;

  const getAdjustedEndDate = (userEndDate: string | Date) => {
    const userDate = new Date(userEndDate);
    const currentDate = new Date();

    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      userDate.setMonth(userDate.getMonth() - 1);
    }

    return userDate.toISOString();
  };

  const getConnectorConfigCarbon = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: isSitePremium
          ? 'portfolio-carbon-site-summary'
          : 'portfolio-carbon-new',
        options: {
          ...params,
          scopeType: 'all',
          dataSource: isSitePremium ? dataSource : ''
        }
      };
    } else {
      return {
        name: isSitePremium
          ? 'portfolio-carbon-site-summary'
          : 'portfolio-carbon-site',
        options: {
          ...params,
          allDetails: 'true',
          dataSource: isSitePremium ? dataSource : ''
        }
      };
    }
  };

  const getConnectorConfigGraph = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'portfolio-carbon-new-graph',
        options: {
          ...params,
          scopeType: 'all',
          dataSource
        }
      };
    } else {
      return {
        name: 'portfolio-carbon-site-graph',
        options: {
          ...params,
          allDetails: 'true',
          dataSource
        }
      };
    }
  };

  const getConnectorConfigTable = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'portfolio-carbon-new-table',
        options: {
          ...params,
          groupType: 'category',
          dataSource
        }
      };
    } else {
      return {
        name: 'portfolio-carbon-site-table',
        options: {
          ...params,
          allDetails: 'true',
          dataSource
        }
      };
    }
  };

  useEffect(() => {
    if (isMounted) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
      if (isSitePremium) {
        dataConnectorForGraph &&
          dataConnectorForGraph.actions &&
          dataConnectorForGraph.actions.getAll();
        dataConnectorForTable &&
          dataConnectorForTable.actions &&
          dataConnectorForTable.actions.getAll();
      }
    }
  }, [requestContext, dataSource]);

  console.log('dataSource', dataSource);

  connectorConfig = getConnectorConfigCarbon(
    toggleFeatureScopeOne,
    requestContext
  );

  connectorConfigForGraph = getConnectorConfigGraph(
    toggleFeatureScopeOne,
    requestContext
  );

  connectorConfigForTable = getConnectorConfigTable(
    toggleFeatureScopeOne,
    requestContext
  );
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  let dataConnectorForGraph: any;
  let dataConnectorForTable: any;

  [dataConnectorForGraph] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForGraph,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  [dataConnectorForTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForTable,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  const c02AvoidanceData:
    | ICarbonAvoidanceData
    | undefined = isPremiumSubscription
    ? CarbonAvoidanceService(bearerToken ?? 'bearer ', requestContext)
    : undefined;

  return (
    <div>
      {isSitePremium ? (
        <TotalEmissionDetailWidgetSeparatePortfolio
          dataConnector={dataConnector}
          dataConnectorForGraph={dataConnectorForGraph}
          dataConnectorForTable={dataConnectorForTable}
          avoidanceData={c02AvoidanceData}
          requestContext={requestContext}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
        />
      ) : (
        <TotalEmissionsDetailsComponent
          dataConnector={dataConnector}
          avoidanceData={c02AvoidanceData}
          requestContext={requestContext}
        />
      )}
      {isSitePremium ? (
        <PortfolioCarbonScopeTwoSavingsBySiteComponentSeparate
          dataConnector={dataConnector}
          dataConnectorForTable={dataConnectorForTable}
          UtilityBillType={UtilityType.CO2}
        />
      ) : (
        <PortfolioCarbonScopeTwoSavingsBySiteComponent
          dataConnector={dataConnector}
          UtilityBillType={UtilityType.CO2}
        />
      )}
      {isPremiumSubscription && (
        <CarbonAvoidanceSummaryWidget
          bearerToken={bearerToken ?? 'bearer '}
          requestContext={requestContext}
        />
      )}
    </div>
  );
};
export default PortfolioTotalEmissionsDetailsComponent;
